import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Chip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { PayrollService } from '../../services/payroll.service';
import { ISalaryComponent } from '../../interfaces/payroll.interface';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import SalaryComponentForm from './SalaryComponentForm';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';

const SalaryComponentList: React.FC = () => {
    const [components, setComponents] = useState<ISalaryComponent[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingComponent, setEditingComponent] = useState<ISalaryComponent | null>(null);
    const { t } = useTranslation(['payroll', 'common']);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const loadComponents = async () => {
        if (!institutionId) return;
        try {
            const data = await PayrollService.getSalaryComponents(institutionId);
            setComponents(data);
        } catch (error) {
            showApiError(error, t('shared.loadComponentsFailed'));
        }
    };

    useEffect(() => { loadComponents(); }, [institutionId]);

    const handleAdd = () => {
        setEditingComponent(null);
        setDialogOpen(true);
    };

    const handleEdit = (component: ISalaryComponent) => {
        setEditingComponent(component);
        setDialogOpen(true);
    };

    const handleDelete = async (id: string) => {
        try {
            await PayrollService.deleteSalaryComponent(id);
            showSuccess(t('salaryComponentList.toast.deleted'));
            loadComponents();
        } catch (error) {
            showApiError(error, t('salaryComponentList.toast.deleteFailed'));
        }
    };

    const handleSeedDefaults = async () => {
        try {
            await PayrollService.seedDefaultSalaryComponents(institutionId);
            showSuccess(t('salaryComponentList.toast.seeded'));
            loadComponents();
        } catch (error) {
            showApiError(error, t('salaryComponentList.toast.seedFailed'));
        }
    };

    const handleSave = async (data: Partial<ISalaryComponent>) => {
        try {
            if (editingComponent) {
                await PayrollService.updateSalaryComponent(editingComponent.id, data);
                showSuccess(t('salaryComponentList.toast.updated'));
            } else {
                await PayrollService.createSalaryComponent(data, institutionId);
                showSuccess(t('salaryComponentList.toast.created'));
            }
            setDialogOpen(false);
            loadComponents();
        } catch (error) {
            showApiError(error, t('salaryComponentList.toast.saveFailed'));
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('salaryComponentList.title')}</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {isSystemAdmin && (
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>{t('shared.selectInstitution')}</InputLabel>
                            <Select
                                value={institutionId}
                                onChange={(e) => setInstitutionId(e.target.value)}
                                label={t('shared.selectInstitution')}
                            >
                                {institutions.map((inst) => (
                                    <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {isSystemAdmin && (
                        <Button variant="outlined" onClick={handleSeedDefaults} disabled={!institutionId}>
                            {t('salaryComponentList.seedDefaults')}
                        </Button>
                    )}
                    <Button variant="contained" color="primary" onClick={handleAdd} disabled={!institutionId}>
                        {t('shared.addComponent')}
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('common:fields.name')}</TableCell>
                            <TableCell>{t('common:fields.type')}</TableCell>
                            <TableCell>{t('common:fields.status')}</TableCell>
                            <TableCell align="right">{t('common:fields.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {components.map((comp) => (
                            <TableRow key={comp.id}>
                                <TableCell>{comp.name}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(`componentType.${comp.type}`)}
                                        color={comp.type === 'EARNING' ? 'success' : 'error'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={comp.isActive ? t('common:status.active') : t('common:status.inactive')}
                                        color={comp.isActive ? 'primary' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleEdit(comp)} size="small" aria-label={t('salaryComponentList.edit')}>
                                        <EditIcon />
                                    </IconButton>
                                    {isSystemAdmin && (
                                        <IconButton onClick={() => handleDelete(comp.id)} size="small" color="error" aria-label={t('salaryComponentList.delete')}>
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {components.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">{t('salaryComponentList.empty')}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <SalaryComponentForm
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSave}
                component={editingComponent}
            />
        </Box>
    );
};

export default SalaryComponentList;
